import React, { useEffect, useState, useMemo } from 'react';
import { RouteChildrenProps } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import api from 'utils/api-services';
import { Category, SubCategory, Topic } from 'types';
import { MainContentPageLayout } from './MainContentPageLayout';
import { useCategories } from 'modules/hooks';
import { LeftNavLayout } from 'modules/pages';

interface RouteParams {
  categoryId?: string;
  topicId?: string;
}

const MainContentPageContainer: React.FunctionComponent<RouteChildrenProps<RouteParams>> = (
  props
) => {
  const { match } = props;
  const intl = useIntl();

  let currentCategoryId: number | string | undefined;
  let currentTopicId: number | undefined;
  if (match) {
    currentCategoryId = match.params.categoryId && (Number(match.params.categoryId) || undefined);
    currentTopicId = Number(match.params.topicId) || undefined;
  }

  const { categories, areCategoriesLoading } = useCategories();

  const [subcategories, setSubcategories] = useState<SubCategory[]>([]);
  const [currentTopic, setCurrentTopic] = useState<Topic | undefined>();

  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (currentTopicId) {
      setIsLoading(true);
      api
        .getTopic(currentTopicId)
        .then((res) => {
          setCurrentTopic(res as Topic);
          setIsLoading(false);
        })
        .catch(() => setError('Invalid topic'));
    } else {
      // clear topic when routing from a topic view to a category view
      setCurrentTopic(undefined);
    }
  }, [currentTopicId]);

  useEffect(() => {
    if (!currentTopicId) {
      setIsLoading(true);
      if (currentCategoryId) {
        api
          .getFaqCategory(currentCategoryId)
          .then((res) => {
            setSubcategories(
              res.map((subcategory) => ({
                ...subcategory,
                categoryId: currentCategoryId,
              })) as SubCategory[]
            );
            setIsLoading(false);
          })
          .catch(() => setError('Invalid category'));
      } else {
        api
          .getMostPopular()
          .then((res) => {
            const topicsSortedAlphabetically = (res as Topic[]).sort((a, b) =>
              a.title.localeCompare(b.title)
            );
            const subcategories = [
              {
                id: 0,
                name: intl.formatMessage(messages.mostPopular),
                topics: topicsSortedAlphabetically,
              },
            ];
            setSubcategories(subcategories as SubCategory[]);
            setIsLoading(false);
          })
          .catch(() => setError('Invalid category'));
      }
    }
  }, [currentCategoryId, currentTopicId, intl.formatMessage]);

  const currentCategoryName = useMemo(() => {
    const currentCategory = categories.find((cat: Category) => cat.id === currentCategoryId);
    if (currentCategory) {
      return currentCategory.name;
    }
  }, [categories, currentCategoryId]);

  if (error) console.log(error);

  // avoid rendering subcategories if they don't match the currently selected category
  const currentSubcategories =
    subcategories.length && subcategories[0].categoryId === currentCategoryId ? subcategories : [];

  return (
    <LeftNavLayout activeCategoryId={currentCategoryId}>
      <MainContentPageLayout
        currentCategoryName={currentCategoryName}
        currentSubCategories={currentSubcategories}
        currentTopic={currentTopic}
        isLoading={isLoading || areCategoriesLoading}
      />
    </LeftNavLayout>
  );
};

export default MainContentPageContainer;

const messages = defineMessages({
  mostPopular: {
    id: 'src/modules/pages/MainContentPage/MainContentPageContainer:mostPopular',
    description: 'most popular category text',
    defaultMessage: 'Most Popular',
  },
});
